import {
    AlignmentType,
    Document,
    HeadingLevel,
    Paragraph,
    TabStopType,
    TextRun,
    ImageRun,
    WidthType,
    Tab,
    Table,
    TableRow,
    PageNumber,
    TableCell,
    BorderStyle,
    VerticalAlign,
    Footer,
} from "docx";
import dayjs from "dayjs";
import { allCapitalize, changeNameFormat, toTitleCase } from 'utils/Utils';

const DEEP_BLUE_COLOR = "002149";
const BLACK_COLOR = "000000";
const LIGHT_BLUE_COLOR = "#e6ebfe";
const WHITE_COLOR = "#ffffff";

export class DocumentCreator {
    /**
    * Creates a Word document containing user profile information such as personal details, experiences, educations,
    * professional summary, certifications, custom sections, and technical competencies.
    * The document is formatted with specific styles and includes a footer with page numbers.
    * 
    * @param {Object} params - The parameters for creating the document.
    * @param {ArrayBuffer} params.imageArrayBuffer - The image data buffer for the user's profile picture.
    * @param {Array} params.experiences - An array of work experience objects.
    * @param {Array} params.educations - An array of education details.
    * @param {Object} params.professional - Professional summary details.
    * @param {Array} params.certifications - An array of certification details.
    * @param {Object} params.user - User's personal information including name, phone, email, and city.
    * @param {Array} params.customSection - An array of custom section details.
    * @param {Array} params.technicalCompetencies - An array of technical competency details.
    * 
    * @returns {Promise<Document>} - A promise that resolves to a Document object containing the user's profile information.
    */

    async create({ imageArrayBuffer, experiences, educations, professional, certifications, user, customSection, technicalCompetencies }) {

        const userHeading = await this.createuserHeading(imageArrayBuffer, user.name, user.phone, user.email, user.city);
        const document = new Document({
            styles: {
                default: {
                    document: {
                        run: {
                            font: "Times New Roman",
                            color: DEEP_BLUE_COLOR,
                        },
                    },
                },
                paragraphStyles: [
                    {
                        id: "Heading1",
                        name: "Heading 1",
                        basedOn: "Normal",
                        next: "Normal",
                        quickFormat: true,
                        run: {
                            size: 21,
                            bold: true,
                            color: BLACK_COLOR,
                        },
                        paragraph: {
                            spacing: {
                                after: 20,
                                before: 200,
                            },
                        },
                    },
                    {
                        id: "Heading2",
                        name: "Heading 2",
                        basedOn: "Normal",
                        next: "Normal",
                        quickFormat: true,
                        run: {
                            size: 20,
                            bold: true,
                        },
                        paragraph: {
                            spacing: {

                            },
                        },
                    },]
            },

            sections: [
                {
                    properties: {
                        page: {
                            margin: {
                                top: 450,
                                right: 450,
                                bottom: 450,
                                left: 450,
                            },
                        },
                    },
                    footers: {
                        default: new Footer({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            children: [`${user?.name}`],
                                            bold: true,
                                            color: BLACK_COLOR,
                                        }),
                                        new TextRun({
                                            children: [new Tab(), "Page "],
                                            bold: true,
                                        }),
                                        new TextRun({
                                            children: [PageNumber.CURRENT],
                                            bold: true,
                                        }),
                                        new TextRun({
                                            children: [" of "],
                                            bold: true,
                                        }),
                                        new TextRun({
                                            children: [PageNumber.TOTAL_PAGES],
                                            bold: true,
                                        }),
                                    ],
                                    tabStops: [
                                        {
                                            type: TabStopType.RIGHT,
                                            position: 11000,
                                        },
                                    ],
                                }),
                            ],
                        }),
                    },


                    children: [
                        ...userHeading,
                        this.addSemantic(),
                        ...this.createProfessionalSummary(professional),
                        ...this.createEducationTable(educations),
                        ...this.createCertificationTable(certifications),
                        ...this.createWorkExperienceTable(experiences),
                        ...this.createResponsibilityTable(experiences),
                        ...this.createCustomSection(customSection),
                        ...this.createSkills(experiences),
                        ...this.createTechnicalCompetenciesTable(technicalCompetencies),
                    ]
                }
            ]
        });

        return document;
    }
    /**
    * Chunks an array into smaller arrays of the given size.
    * @param {Array} array - The array to be chunked.
    * @param {number} size - The size of each chunk.
    * @returns {Array} - An array of arrays, each of which is a chunk of the original array.
    */
    chunkArray(array, size) {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
        return result;
    }

    truncateString = (strIndustryName) => {
        if (!strIndustryName) return "";
        const words = strIndustryName.split(/\s+/).filter(word => /^[a-zA-Z]+$/.test(word));
        return toTitleCase(words.slice(0, 2).join(" "));
    }

    /**
    * Creates a table with technical competencies of the user.
    * Splits the array into chunks of 6 elements and creates a table with 7 columns.
    * Each competency is centered and has a bold font.
    * The table has a shading of #fafaff and a border of LIGHT_BLUE_COLOR.
    * @param {Array<string>} technicalCompetencies - The array of technical competencies of the user.
    * @returns {Array<Paragraph | Table>} - An array of a heading and a table.
    */
    createTechnicalCompetenciesTable(technicalCompetencies) {
        if (!technicalCompetencies || technicalCompetencies.length === 0) return [];
        const heading = this.createHeading("TECHNICAL COMPETENCIES")

        // Split the array into chunks of 6 elements
        const chunkedCompetencies = this.chunkArray(technicalCompetencies, 7);

        // Initialize an empty rows array with explicit type annotation
        const rows = [];

        // Generate the first row for each chunked competency set
        chunkedCompetencies.forEach((chunk, index) => {
            const cells = chunk.flatMap((competency) => [
                // Competency cell
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: competency,
                                    size: 20,
                                    bold: true,
                                }),
                            ],
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                    verticalAlign: VerticalAlign.CENTER,
                    margins: { top: 50, bottom: 50, left: 20, right: 20 },
                    width: { size: 500, type: WidthType.DXA },
                    shading: { fill: "#fafaff" },
                    borders: {
                        top: { color: LIGHT_BLUE_COLOR, size: 8, style: BorderStyle.SINGLE },
                        bottom: { color: LIGHT_BLUE_COLOR, size: 8, style: BorderStyle.SINGLE },
                        left: { color: LIGHT_BLUE_COLOR, size: 8, style: BorderStyle.SINGLE },
                        right: { color: LIGHT_BLUE_COLOR, size: 8, style: BorderStyle.SINGLE },
                    },
                }),

                // Empty cell with reduced width
                new TableCell({
                    children: [],
                    width: { size: 50, type: WidthType.DXA },
                    shading: { fill: WHITE_COLOR },
                    borders: {
                        top: { color: WHITE_COLOR, size: 8, style: BorderStyle.SINGLE },
                        bottom: { color: WHITE_COLOR, size: 8, style: BorderStyle.SINGLE },
                        left: { color: WHITE_COLOR, size: 8, style: BorderStyle.SINGLE },
                        right: { color: WHITE_COLOR, size: 8, style: BorderStyle.SINGLE },
                    },
                }),
            ]);

            // Push a new row for each chunked set
            rows.push(new TableRow({
                children: cells,
            }));

            // After the first row, add the invisible gap row (except after the last chunk)
            if (index < chunkedCompetencies.length - 1) {
                rows.push(new TableRow({
                    children: Array(7).fill(
                        new TableCell({
                            children: [],
                            width: { size: 100, type: WidthType.DXA },
                            shading: { fill: WHITE_COLOR },
                            borders: {
                                top: { color: WHITE_COLOR, size: 8, style: BorderStyle.SINGLE },
                                bottom: { color: WHITE_COLOR, size: 8, style: BorderStyle.SINGLE },
                                left: { color: WHITE_COLOR, size: 8, style: BorderStyle.SINGLE },
                                right: { color: WHITE_COLOR, size: 8, style: BorderStyle.SINGLE },
                            },
                        })
                    ),
                }));
            }
        });

        // Create the table with the rows and return
        return [heading, new Table({
            rows: rows,
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
        })];
    }
    /**
    * Returns a Word document paragraph with the professional summary text.
    * @param {string} summary - The professional summary text.
    * @returns {Array<Object>} A Word document paragraph with the professional summary text.
    */
    createProfessionalSummary(summary) {
        if (!summary) return [];
        const heading = this.createHeading("PROFESSIONAL SUMMARY");
        return [
            heading,
            new Paragraph({
                children: [
                    new TextRun({
                        text: summary,
                        size: 22,
                    }),
                ],
            })
        ];
    }
    /**
    * Creates a Word document paragraph with the contact information.
    * @param {string} phoneNumber - The phone number of the user.
    * @param {string} email - The email address of the user.
    * @param {string} address - The address of the user.
    * @returns {Object} A Word document paragraph with the contact information.
    */
    createContactInfo(
        phoneNumber,
        email,
        address,
    ) {
        return new Paragraph({
            alignment: AlignmentType.LEFT,
            children: [
                new TextRun({
                    text: ` ${phoneNumber} | : ${email} |  ${address}`,
                }),
            ]
        });
    }
    /**
    * Adds a thematic break to the document, which is a horizontal line that 
    * separates sections of the document.
    * @returns {Object} A Word document paragraph with a thematic break.
    */
    addSemantic() {
        return new Paragraph({
            thematicBreak: true,
        });
    }
    /**
    * Creates a Word document heading with the given text.
    * @param {string} text - The text of the heading.
    * @returns {Object} A Word document paragraph with the given text as a heading.
    */
    createHeading(text) {
        return new Paragraph({
            style: "Heading1",
            text: text,
            heading: HeadingLevel.HEADING_1,
        });
    }
    /**
    * Converts a blob to an array buffer. This is used to convert image blobs
    * to a format that can be used in the Word document.
    * @param {Blob} blob - The blob to convert to an array buffer.
    * @returns {Promise<ArrayBuffer>} A promise that resolves with the array
    * buffer representation of the blob.
    */
    async blobToArrayBuffer(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(blob);
        });
    }
    /**
    * Creates a Word document heading with the user's photo, name, email, phone
    * number, and address. The heading is formatted as a table with two cells:
    * the first cell contains the user's photo, and the second cell contains the
    * user's name, email, phone number, and address. The cells are styled with
    * borders, shading, and alignment.
    * @param {ArrayBuffer} imageArrayBuffer - The image data of the user's photo.
    * @param {string} name - The user's name.
    * @param {string} phone - The user's phone number.
    * @param {string} email - The user's email address.
    * @param {string} address - The user's address.
    * @returns {Array<Object>} An array containing the heading table.
    */
    async createuserHeading(
        imageArrayBuffer,
        name,
        phone,
        email,
        address
    ) {
        const imageBuffer = await this.blobToArrayBuffer(imageArrayBuffer);

        const headingTable = new Table({
            rows: [
                new TableRow({
                    children: [
                        // First cell - the image
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new ImageRun({
                                            data: imageBuffer,
                                            transformation: {
                                                width: 70,
                                                height: 70,
                                            },
                                            type: "jpg",
                                        }),
                                    ],
                                }),
                            ],
                            borders: {
                                top: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                bottom: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                left: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                right: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                            },
                            shading: {
                                fill: WHITE_COLOR,
                            },
                            verticalAlign: VerticalAlign.CENTER,
                            width: { size: 1000, type: WidthType.DXA },
                        }),
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: name,
                                            bold: true,
                                            size: 32,
                                            color: BLACK_COLOR,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `${email ?? ''} | ${phone ? `+91-${phone}` : ''} | ${address ?? ''}`,
                                            size: 22,
                                            color: DEEP_BLUE_COLOR,
                                        }),
                                    ],
                                }),
                            ],
                            borders: {
                                top: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                bottom: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                left: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                right: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                            },
                            shading: {
                                fill: WHITE_COLOR,
                            },
                            width: { size: 8000, type: WidthType.DXA },
                        }),
                    ],
                }),
            ],
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            borders: {
                top: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                bottom: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                left: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                right: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
            },
        });

        return [headingTable];
    }

    /**
    * Creates a table with education history of the user.
    * Each row consists of two cells: one for the main education heading and
    * one for the dates. The main heading cell contains the degree, field of
    * study, and education institute name. If there are any key accomplishments,
    * they are listed as bullet points below the main heading. The right side
    * cell contains the dates of the education period.
    *
    * @param {array} educations - The education history of the user.
    * @returns {array} An array of a heading and a table.
    */
    createEducationTable(educations) {
        if (!educations?.length) return [];

        const heading = this.createHeading("EDUCATION");


        const rows = educations.map((education) => {
            return new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: `${education.degree_received} ${education.field_of_study}`,
                                        bold: true,
                                        size: 20,
                                    }),
                                    new TextRun({
                                        text: `, ${allCapitalize(education?.education_institute_name)}`,
                                        bold: false,
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                                indent: {
                                    left: 80,
                                },
                            }),
                            ...(education.key_accomplishments || []).map(
                                (accomplishment) =>
                                    new Paragraph({
                                        children: [new TextRun({ text: accomplishment, size: 20, })],
                                        bullet: { level: 0 },
                                        alignment: AlignmentType.LEFT,
                                        spacing: { after: 10 },
                                        indent: {
                                            left: 500,
                                            hanging: 300,
                                        },
                                    })
                            ),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            right: { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                        },
                        margins: { top: 20, bottom: 20, left: 50, right: 10 },
                        width: { size: 1700, type: WidthType.DXA },
                    }),

                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: this.createPositionDateText(
                                            education.education_from,
                                            education.education_to,
                                        ),
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.RIGHT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            left: { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        },
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        width: { size: 60, type: WidthType.DXA },
                    }),
                ],
            });
        });

        return [
            heading,
            new Table({
                rows: rows,
                width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                },
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                    bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                    left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                    right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                },
            })];
    }

    /**
    * @description Creates a table for key responsibilities and accomplishments based on work history data
    * @param {array} workHistory - An array of work history objects
    * @returns {array} - A heading and a table with the respective rows
    */
    createResponsibilityTable(workHistory) {
        if (!workHistory?.length) return [];
        const heading = this.createHeading("KEY RESPONSIBILITIES AND ACCOMPLISHMENTS")

        if (!workHistory?.length) return new Table({ rows: [] });
        workHistory = workHistory?.filter(
            wh => wh.organization_name?.toLowerCase() !== "work gap"
        );
        const responsibilities = [];
        let currentGroup = null;

        for (const record of workHistory) {
            const role = {
                job_type: record?.job_type,
                work_history_from: record?.work_history_from ?? "",
                work_history_to: record?.work_history_to ?? "",
                present: record?.present,
                gap_title: record?.gap_title || "",
                team_size_managed: record?.team_size_managed,
                work_history_location: record?.work_history_location,
                last_job_function: record?.last_job_function,
                highest_designation: record?.highest_designation,
                work_oriented_skills: record?.work_oriented_skills || [],
                key_accomplishments: record?.key_accomplishments || [],
            };
            if (
                currentGroup &&
                currentGroup?.organization_name?.toLowerCase() === record.organization_name?.toLowerCase()
            ) {
                const existingWorkIdex = responsibilities?.findIndex(wHistory => wHistory?.organization_name?.toLowerCase() === record.organization_name?.toLowerCase());
                if (existingWorkIdex !== -1) {
                    responsibilities[existingWorkIdex].roles.push(role);
                }
            } else {
                currentGroup = {
                    organization_name: record.organization_name,
                    roles: [role],
                };
                responsibilities.push({
                    organization_name: allCapitalize(record?.organization_name || ''),
                    industry_sector: changeNameFormat(record?.organization_details?.industry_sector || ''),
                    roles: [role]
                });
            }
        }

        const rows = responsibilities.flatMap((response, orgIndex) => {
            const isLastOrg = orgIndex === responsibilities.length - 1;

            const orgRow = new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: `${allCapitalize(response.organization_name)}, `,
                                        bold: true,
                                        size: 20,
                                    }),
                                    new TextRun({
                                        text: `${response?.industry_sector || ""}`,
                                        bold: false,
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        columnSpan: 2,
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        },
                        margins: { top: 20, bottom: 10, left: 50, right: 50 },
                    }),
                ],
                // borders: {
                //     bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                // },
            });

            const roleRows = response.roles.map((role, roleIndex) => {
            const isLastRoleInOrg = roleIndex === response.roles.length - 1;
            const bottomBorder = isLastOrg && isLastRoleInOrg
                ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR }
                : { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR };

                return new TableRow({
                    children: [
                        // Left cell: role and accomplishments
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `${role?.highest_designation || "Designation Unknown"} `,
                                            bold: true,
                                            size: 20,
                                        }),
                                        new TextRun({
                                            text: `${role?.work_history_location ? "(" + role?.work_history_location + ")" : ""} `,
                                            size: 20,
                                        }),
                                    ],
                                    alignment: AlignmentType.LEFT,
                                    spacing: { after: 100 },
                                }),
                                ...role.key_accomplishments.map((accomplishment) =>
                                    new Paragraph({
                                        children: [new TextRun({ text: accomplishment, size: 20 })],
                                        bullet: { level: 0 },
                                        spacing: { after: 100 },
                                        indent: { left: 500, hanging: 300 },
                                    })
                                ),
                            ],
                            borders: {
                                top: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                bottom: bottomBorder,
                                left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                                right: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                            },
                            margins: { top: 20, bottom: 20, left: 50, right: 10 },
                            width: { size: 500, type: WidthType.DXA },
                        }),

                        // Right cell: date for this role
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: this.createPositionDateText(
                                                role.work_history_from,
                                                role.work_history_to,
                                                role.present
                                            ),
                                            size: 20,
                                        }),
                                    ],
                                    alignment: AlignmentType.RIGHT,
                                }),
                            ],
                            borders: {
                                top: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                bottom: bottomBorder,
                                left: { style: BorderStyle.NONE, size: 0, color: LIGHT_BLUE_COLOR },
                                right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            },
                            margins: { top: 20, bottom: 20, left: 10, right: 50 },
                            width: { size: 500, type: WidthType.DXA },
                        }),
                    ],
                });
            });

            return [orgRow, ...roleRows];
        });



        return [heading, new Table({
            rows: rows,
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
            },
        })];
    }

    /**
    * Create a table for work experience summary.
    * @param {object[]} workHistory - Work history array with organization_name, industry_sector, highest_designation, and team_size_managed.
    * @returns {object[]} Two-element array. First element is the heading paragraph, second element is a Table object.
    */
    createWorkExperienceTable(workHistory) {
        if (!workHistory?.length) return [];
        const heading = this.createHeading("WORK EXPERIENCE OVERVIEW ")

        const experiences = workHistory?.filter(
            wh => !(wh.organization_name?.toLowerCase() === "work gap" && !wh.show_in_resume)
        );
        const headers = ["Period", "Company Name", "Industry", "Last Designation", "Team"];
        const columnPercentages = [16, 36, 16, 23, 7];

        // Header Row
        const headerRow = new TableRow({
            children: headers.map((header, index) =>
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: header,
                                    bold: true,
                                    size: 22,
                                }),
                            ],
                            alignment: AlignmentType.LEFT,
                        }),
                    ],
                    borders: {
                        top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                    },
                    margins: { top: 20, bottom: 20, left: 50, right: 50 },
                    width: { size: columnPercentages[index] * 50, type: WidthType.PERCENTAGE, },
                    shading: { fill: LIGHT_BLUE_COLOR },
                })
            ),
        });

        const rows = experiences.map((exp, index) => {
            const isShaded = index % 2 !== 0;
            return new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: this.createPositionDateText(exp.work_history_from, exp.work_history_to, exp.present),
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: columnPercentages[0] * 50, type: WidthType.PERCENTAGE, },
                    }),

                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: exp?.organization_name === 'Work Gap' ? `Career Break: ${exp?.gap_title}` : allCapitalize(exp?.organization_name || ''),
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: columnPercentages[1] * 50, type: WidthType.PERCENTAGE, },
                    }),

                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: exp?.organization_details?.industry_sector ? this.truncateString(exp.organization_details.industry_sector) : "",
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: columnPercentages[2] * 50, type: WidthType.PERCENTAGE, },
                    }),

                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: exp?.organization_name === 'Work Gap' ? '' : exp?.highest_designation,
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: columnPercentages[3] * 50, type: WidthType.PERCENTAGE, },
                    }),

                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: exp?.organization_name === 'Work Gap' ? '' : exp?.team_size_managed === 'Individual contributor (IC)' ? 'IC' : exp?.team_size_managed || "",
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: columnPercentages[4] * 50, type: WidthType.PERCENTAGE, },
                    }),
                ],
            });
        });

        return [heading, new Table({
            rows: [headerRow, ...rows],
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
        })];
    }

    /**
    * Creates a table with work experience skills of the user.
    * Takes an array of work experience objects and groups the skills into categories.
    * The table has headers for expertise, key skills, where learned/used, and experience.
    * Each row is shaded with LIGHT_BLUE_COLOR, and borders are set to LIGHT_BLUE_COLOR.
    * @param {Array<Object>} workExperienceData - An array of work experience objects.
    * @returns {Array<Paragraph | Table>} - An array of a heading and a table.
    */
    createSkills(workExperienceData) {
        if (!workExperienceData?.length) return [];
        const heading = this.createHeading("SKILLS SUMMARY")
        const sortedHistory = workExperienceData?.filter(wh => wh?._id && wh?.organization_name?.toLowerCase() !== "work gap"); //?.sort((a, b) => new Date(a.work_history_from) - new Date(b.work_history_from));

        let skills = this.groupWorkSkills(sortedHistory);
        const headers = ["Expertise", "Key Skills", "Where learned / used?", "Experience"];

        // Header Row
        const headerRow = new TableRow({
            children: headers.map((header, index) =>
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: header,
                                    bold: true,
                                    size: 22,
                                }),
                            ],
                            alignment: AlignmentType.LEFT,
                        }),
                    ],
                    borders: {
                        top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                    },
                    margins: { top: 20, bottom: 20, left: 50, right: 50 },
                    width: { size: 2000, type: WidthType.DXA },
                    shading: { fill: LIGHT_BLUE_COLOR }, // Header shaded
                })
            ),
        });

        // Body Rows with alternating shading and white borders for unshaded rows
        const rows = skills.map((skill, index) => {
            const isShaded = index % 2 !== 0; // Alternate shading for rows
            return new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: skill.expertise,
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: 2000, type: WidthType.DXA },
                    }),

                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: skill.key_skills,
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: 7000, type: WidthType.DXA },
                    }),

                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: skill.where_learned,
                                        size: 18,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: 4500, type: WidthType.DXA },
                    }),

                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: `${skill?.experience < 2 ? skill?.experience + " year" : skill?.experience + " years"} `,
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 0, color: LIGHT_BLUE_COLOR },
                            left: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: isShaded ? { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR } : { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        shading: { fill: isShaded ? LIGHT_BLUE_COLOR : WHITE_COLOR },
                        width: { size: 10, type: WidthType.DXA },
                    }),
                ],
            });
        });

        return [heading, new Table({
            rows: [headerRow, ...rows],
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
        })];
    }

    groupWorkSkills = (workHistories) => {
        // Grouping and aggregating data using find
        const groupedData = [];

        workHistories.forEach(({ last_job_function, organization_name, skills, work_history_from, work_history_to }) => {
            if (!skills?.length) return;
            // Find the existing group for this role
            let group = groupedData.find(g => g.last_job_function === last_job_function);

            if (!group) {
                // If no group exists, create a new one
                group = { last_job_function, organization_name: new Set(), skills: new Set(), experience: 0 };
                groupedData.push(group);
            }

            // Add unique companies
            group.organization_name.add(organization_name || "");

            // Add unique skills
            skills.forEach(skill => group.skills.add(skill));

            // Add experience
            group.experience += this.calculateExperience(work_history_from, work_history_to);
        });

        return groupedData?.map(wh => {
            return {
                expertise: wh?.last_job_function,
                key_skills: Array.from(wh?.skills?.values() || [])?.join(', '),
                where_learned: Array.from(wh?.organization_name?.values() || []).join(', '),
                experience: wh?.experience
            };
        });
    };

    calculateExperience = (start, end) => {
        const fromDate = start ? dayjs(start) : dayjs(new Date());
        const toDate = end ? dayjs(end) : dayjs(new Date());

        // Calculate the difference in months
        const experience = (toDate.diff(fromDate, 'month') / 12).toFixed(0);
        return parseInt(experience, 10);
    };

    /**
    * Creates a Word document subheading with the provided text.
    * @param {string} text - The text of the subheading.
    * @returns {Object} A Word document paragraph with the given text as a subheading.
    */

    createSubHeading(text) {
        return new Paragraph({
            text: text,
            heading: HeadingLevel.HEADING_2
        });
    }

    /**
    * Creates a table with certifications of the user.
    * Each row contains the certification received, field of study, and education institute name.
    * @param {Array<Object>} certifications - The certifications of the user. Each element in the array
    * should have fields "certificate_received", "field_of_study", and "education_institute_name".
    * @returns {Array<Paragraph | Table>} - An array of a heading and a table.
    */
    createCertificationTable(certifications) {
        if (!certifications?.length) return [];
        const heading = this.createHeading("CERTIFICATIONS")

        const rows = certifications.map((cert) => {
            return new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: `${cert.certificate_received} (${cert.field_of_study}), `,
                                        size: 20,
                                        bold: true,
                                    }),
                                    new TextRun({
                                        text: `${allCapitalize(cert.education_institute_name)} `,
                                        size: 20,
                                        bold: false,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            right: { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR }, // inner border
                        },
                        margins: { top: 20, bottom: 20, left: 50, right: 50 },
                        width: { size: 3000, type: WidthType.DXA },
                    }),
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: this.createPositionDateText(
                                            cert.education_from,
                                            cert.education_to,
                                        ),
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.RIGHT,
                            }),
                        ],
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                            left: { style: BorderStyle.SINGLE, size: 8, color: WHITE_COLOR },
                            right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                        },
                        margins: { top: 20, bottom: 20, left: 20, right: 50 },
                        width: { size: 1000, type: WidthType.DXA },
                    }),
                ],
            });
        });

        return [heading, new Table({
            rows: rows,
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                bottom: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                left: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
                right: { style: BorderStyle.SINGLE, size: 8, color: LIGHT_BLUE_COLOR },
            },
        })];
    }

    /**
    * Creates a Word document section from an array of custom section objects.
    * Each custom section object should have a key and a message property.
    * The key is uppercased and used as the heading for the section.
    * The message is converted into a Word document paragraph with bullet points.
    * @param {Array<Object>} customSections - An array of custom section objects with key and message properties.
    * @returns {Array<Object>} An array of Word document objects.
    */
    createCustomSection(customSections) {
        const sections = customSections.flatMap(section => {
            const heading = this.createHeading(allCapitalize(section.key));

            const bulletPoints = this.extractListItems(section.message).map(item =>
                new Paragraph({
                    children: [
                        new TextRun({
                            text: item,
                            size: 22,
                        }),
                    ],
                    bullet: {
                        level: 0,
                    },
                    indent: {
                        left: 300,
                        hanging: 200,
                    },
                })
            );

            return [heading, ...bulletPoints];
        });

        return sections;
    }

    // Utility function to extract list items from HTML
    extractListItems(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const listItems = doc.querySelectorAll("li");
        return Array.from(listItems).map(li => li.textContent || "");
    }

    /**
    * Creates a string representing a position's date range.
    * @param {Date} startDate - The start date of the position.
    * @param {Date} endDate - The end date of the position.
    * @param {boolean} present - Whether the position is still active.
    * @returns {string} A string representing the position's date range, e.g. "Jun '21’ - Present".
    */
    createPositionDateText(
        startDate,
        endDate,
        present = false
    ) {
        const startDateText =
            dayjs(startDate).format("MMM YY") + "’ - " || " ";

        const endDateText = present
            ? "Present" :
            dayjs(endDate).format("MMM YY") + "’" || " ";
        return `${startDateText} ${endDateText} `;
    }
}
